import Excel from "exceljs";
import { saveAs } from "file-saver";
import { UtilidadesHelper } from "helpers/UtilidadesHelper";

const headerFill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "354887" }, // Azul oscuro
};

const WHITE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFFFFF" },
};

const allBorders = {
  top: { style: "thin", color: { argb: "FFC0C0C0" } },
  left: { style: "thin", color: { argb: "FFC0C0C0" } },
  bottom: { style: "thin", color: { argb: "FFC0C0C0" } },
  right: { style: "thin", color: { argb: "FFC0C0C0" } },
};

const WHITE_COLOR = "FFFFFFFF";
const NEUTRAL_COLOR = "FF333333";

const EDITABLE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFF9C4" }, // Amarillo claro — celda editable
};

const adStateFills = {
  NPI: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F2FD" } },
  "Early stage": {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFCE4EC" },
  },
  "High Growth": {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3E5F5" },
  },
  "Profitable core": {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE8F5E9" },
  },
  Exit: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3E0" } },
};

export const ExportCampaignManager = {
  ExportCampaigns: async (rows) => {
    try {
      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet("Campaign Manager");

      const columns = [
        { header: "ID", key: "id", width: 10 },
        { header: "Activa", key: "active", width: 10 },
        { header: "Campaña", key: "campaignName", width: 40 },
        { header: "Ad State", key: "adState", width: 18 },
        { header: "Presupuesto diario", key: "dailyBudget", width: 20 },
        { header: "ROAS Objetivo", key: "targetRoas", width: 16 },
        { header: "GAP Mensual %", key: "gapMonthlyPct", width: 16 },
        { header: "GAP Mensual Actual", key: "gapMonthlyCurrent", width: 20 },
        { header: "GAP Mensual Total", key: "gapMonthlyTotal", width: 20 },
        { header: "GAP Diario %", key: "gapDailyPct", width: 14 },
        { header: "GAP Diario Actual", key: "gapDailyCurrent", width: 18 },
        { header: "GAP Diario Total", key: "gapDailyTotal", width: 18 },
        { header: "CPC", key: "cpc", width: 12 },
        { header: "CVR", key: "cvr", width: 12 },
        { header: "Contribución Ads %", key: "salesAds", width: 20 },
        { header: "Contribución Orgánico %", key: "salesOrganic", width: 22 },
        { header: "ROAS", key: "roas", width: 12 },
        { header: "ACOS", key: "acos", width: 12 },
        { header: "Recomendación", key: "recommendation", width: 50 },
      ];

      worksheet.columns = columns;

      // Fila 1: Título
      const totalColumns = columns.length;
      worksheet.mergeCells(1, 1, 1, totalColumns);
      const optionsCell = worksheet.getCell(1, 1);
      optionsCell.value = "Detalle de Campañas (Campaign Manager)";
      optionsCell.font = {
        italic: true,
        bold: true,
        color: { argb: "FF808080" },
        size: 11,
      };
      optionsCell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      worksheet.getRow(1).height = 25;

      // Fila 2: Encabezados
      const headerRow = worksheet.addRow(columns.map((c) => c.header));
      headerRow.eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = { bold: true, color: { argb: WHITE_COLOR } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = allBorders;
      });
      worksheet.getRow(2).height = 20;

      rows.forEach((row) => {
        const isActive = row.active === true;

        const rowData = {
          id: row.id ?? "",
          active: isActive ? "SI" : "NO",
          campaignName: row.campaignName || "",
          adState: row.adState || "",
          dailyBudget: row.dailyBudget ?? "",
          targetRoas: row.targetRoas ?? "",
          gapMonthlyPct: row.gapConsumed?.percentage ?? "",
          gapMonthlyCurrent: row.gapConsumed?.current ?? "",
          gapMonthlyTotal: row.gapConsumed?.total ?? "",
          gapDailyPct: row.dailygapconsumed?.percentage ?? "",
          gapDailyCurrent: row.dailygapconsumed?.current ?? "",
          gapDailyTotal: row.dailygapconsumed?.total ?? "",
          cpc: row.cpc?.value ?? "",
          cvr: row.cvr?.value ?? "",
          salesAds: row.salesContribution?.adsPercentage ?? "",
          salesOrganic: row.salesContribution?.organicPercentage ?? "",
          roas: row.roas?.value ?? "",
          acos: row.acos?.value ?? "",
          recommendation: row.recomendation || "",
        };

        const addedRow = worksheet.addRow(rowData);

        // Estilo base
        addedRow.eachCell((cell) => {
          cell.border = allBorders;
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.fill = WHITE_FILL;
          cell.font = { bold: true, color: { argb: NEUTRAL_COLOR } };
        });

        // Activa: verde / rojo
        const activeCell = addedRow.getCell("active");
        activeCell.font = {
          bold: true,
          color: { argb: isActive ? "FF00B050" : "FFf43360" },
        };

        const adStateCell = addedRow.getCell("adState");
        const adStateFill = adStateFills[row.adState];
        if (adStateFill) adStateCell.fill = adStateFill;

        const nameCell = addedRow.getCell("campaignName");
        nameCell.alignment = { horizontal: "left", vertical: "middle" };

        const recCell = addedRow.getCell("recommendation");
        recCell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };

        // Celdas editables:
        const budgetCell = addedRow.getCell("dailyBudget");
        budgetCell.protection = { locked: false };
        budgetCell.fill = EDITABLE_FILL;

        const roasCell = addedRow.getCell("targetRoas");
        roasCell.protection = { locked: false };
        roasCell.fill = EDITABLE_FILL;
      });

      // bloquea todo excepto las celdas desbloqueadas
      await worksheet.protect("", {
        selectLockedCells: true,
        selectUnlockedCells: true,
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const fechaParaNombre = UtilidadesHelper.fechaParaVistaHoy();
      const blob = new Blob([buffer], { type: fileType });
      saveAs(blob, `CampaignManager_${fechaParaNombre}.xlsx`);
    } catch (error) {
      console.error("❌ Error al Exportar Campaign Manager:", error);
    }
  },
};
